import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Users, ArrowRightLeft, Search } from 'lucide-react';
import { getCities } from '@/services/routeService';
import { MAX_TICKETS_PER_PERSON } from '@/services/bookingService';
import { getToday } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import CustomSelect from '@/components/ui/CustomSelect';
import DatePicker from '@/components/ui/DatePicker';

interface Props {
  variant?: 'hero' | 'compact';
  initialFrom?: string;
  initialTo?: string;
  initialDate?: string;
  initialPassengers?: number;
  /**
   * Re-run the search as soon as a field changes, instead of waiting for the
   * button. Only meaningful when the form is already sitting on the results
   * page (the compact variant), where the `initial*` props mirror the URL —
   * that's what makes "state diverged from the URL" a safe trigger.
   */
  autoSearch?: boolean;
}

/** How long a field change sits still before we navigate. */
const AUTO_SEARCH_DELAY_MS = 250;

const fallbackCities = ['Dhaka', 'Chattogram', 'Chittagong', "Cox's Bazar", 'Sylhet', 'Rajshahi', 'Khulna', 'Feni', 'Comilla', 'Lakshmipur', 'Noakhali'];

export default function SearchForm({
  variant = 'hero',
  initialFrom = '',
  initialTo = '',
  initialDate = '',
  initialPassengers = 1,
  // Without the initial props there is no URL to diverge from, so a bare
  // compact form (tests, any future embed) stays button-driven.
  autoSearch = variant === 'compact' && Boolean(initialFrom && initialTo && initialDate),
}: Props) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [from, setFrom] = useState(initialFrom || 'Feni');
  const [to, setTo] = useState(initialTo || 'Chattogram');
  const [date, setDate] = useState(initialDate || getToday());
  const [passengers, setPassengers] = useState(initialPassengers);
  const [cities, setCities] = useState<string[]>(() => {
    const base = [...fallbackCities];
    if (initialFrom && !base.includes(initialFrom)) base.push(initialFrom);
    if (initialTo && !base.includes(initialTo)) base.push(initialTo);
    return base;
  });

  useEffect(() => {
    getCities().then(c => {
      if (c.length > 0) {
        const merged = [...c];
        if (initialFrom && !merged.includes(initialFrom)) merged.push(initialFrom);
        if (initialTo && !merged.includes(initialTo)) merged.push(initialTo);
        setCities(merged);
      }
    });
  }, [initialFrom, initialTo]);


  const swap = useCallback(() => { setFrom(to); setTo(from); }, [from, to]);

  const runSearch = useCallback((replace: boolean) => {
    if (from === to) return;
    navigate(
      `/search?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&date=${date}&passengers=${passengers}`,
      { replace },
    );
  }, [from, to, date, passengers, navigate]);

  const handleSearch = useCallback(() => runSearch(false), [runSearch]);

  /*
   * Keep the form honest when the URL moves without us — the "try nearby
   * dates" links on the results page, plus browser back/forward. Comparing
   * against the previous props (not against state) means a field the user just
   * edited isn't snapped back before the navigation lands.
   */
  const lastPropsRef = useRef({ initialFrom, initialTo, initialDate, initialPassengers });
  useEffect(() => {
    const prev = lastPropsRef.current;
    lastPropsRef.current = { initialFrom, initialTo, initialDate, initialPassengers };
    if (initialFrom && initialFrom !== prev.initialFrom) setFrom(initialFrom);
    if (initialTo && initialTo !== prev.initialTo) setTo(initialTo);
    if (initialDate && initialDate !== prev.initialDate) setDate(initialDate);
    if (initialPassengers !== prev.initialPassengers) setPassengers(initialPassengers);
  }, [initialFrom, initialTo, initialDate, initialPassengers]);

  /*
   * Auto-search: on the results page the props are the URL, so any divergence
   * means the user changed a field and the results below are now stale. Replace
   * rather than push, so tweaking the date four times doesn't bury the previous
   * page under four history entries.
   */
  useEffect(() => {
    if (!autoSearch) return;
    if (from === to) return;
    const matchesUrl =
      from === initialFrom && to === initialTo &&
      date === initialDate && passengers === initialPassengers;
    if (matchesUrl) return;
    const timer = setTimeout(() => runSearch(true), AUTO_SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [autoSearch, from, to, date, passengers, initialFrom, initialTo, initialDate, initialPassengers, runSearch]);

  const isHero = variant === 'hero';

  // Build options for custom selects
  const fromOptions = cities.filter(c => c !== to).map(c => ({ value: c, label: c }));
  const toOptions = cities.filter(c => c !== from).map(c => ({ value: c, label: c }));
  // Star Line allows a maximum of 3 tickets per person per departure.
  const passengerOptions = Array.from(
    { length: MAX_TICKETS_PER_PERSON },
    (_, i) => ({ value: String(i + 1), label: String(i + 1) }),
  );

  return (
    <div className={`${isHero ? 'glass-card p-6 md:p-8' : 'bg-card border border-border rounded-xl p-4'}`}>
      <div className={`grid gap-4 ${isHero ? 'md:grid-cols-[1fr_auto_1fr_1fr_auto_auto]' : 'md:grid-cols-[1fr_auto_1fr_1fr_auto_auto]'} items-end`}>
        {/* From */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('from')}</label>
          <CustomSelect
            options={fromOptions}
            value={from}
            onChange={setFrom}
            icon={<MapPin className="w-4 h-4 text-primary" />}
            title="Select departure city"
          />
        </div>

        {/* Swap */}
        <button onClick={swap} title="Swap origin and destination" className="flex items-center justify-center w-11 h-11 min-w-[44px] min-h-[44px] rounded-full bg-secondary hover:bg-primary/20 transition-colors self-end mb-0.5">
          <ArrowRightLeft className="w-5 h-5 text-primary" />
        </button>

        {/* To */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('to')}</label>
          <CustomSelect
            options={toOptions}
            value={to}
            onChange={setTo}
            icon={<MapPin className="w-4 h-4 text-accent-strong" />}
            title="Select destination city"
          />
        </div>

        {/* Date */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('date')}</label>
          <DatePicker
            value={date}
            onChange={setDate}
            min={getToday()}
          />
        </div>

        {/* Passengers */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('passengers')}</label>
          <CustomSelect
            options={passengerOptions}
            value={String(passengers)}
            onChange={v => setPassengers(Number(v))}
            icon={<Users className="w-4 h-4 text-muted-foreground" />}
            title="Select number of passengers"
          />
        </div>

        {/* Search */}
        <button
          onClick={handleSearch}
          disabled={from === to}
          className="bg-primary text-primary-foreground rounded-lg px-6 py-3 font-semibold text-base hover:bg-primary/90 transition-colors btn-primary-glow flex justify-center items-center gap-2 mt-2 md:mt-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Search className="w-4 h-4" />
          {t('search')}
        </button>
      </div>
      {from === to && (
        <p className="text-xs text-destructive mt-2">{t('same_origin_dest')}</p>
      )}
    </div>
  );
}
